import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pencil, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export function EditWorkerDialog({ open, onOpenChange, worker, onSuccess }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    color: '#3b82f6',
    hourly_rate: '',
    cin_number: '',
    home_address: '',
    payment_type: 'hourly',
    percentage_rate: '',
    works_saturday: false,
    works_sunday: false,
    new_password: '',
  });

  useEffect(() => {
    if (worker && open) {
      setFormData({
        first_name: worker.first_name || '',
        last_name: worker.last_name || '',
        email: worker.email || '',
        phone: worker.phone || '',
        color: worker.color || '#3b82f6',
        hourly_rate: worker.hourly_rate?.toString() || '',
        cin_number: worker.cin_number || '',
        home_address: worker.home_address || '',
        payment_type: worker.payment_type || 'hourly',
        percentage_rate: worker.percentage_rate?.toString() || '',
        works_saturday: worker.works_saturday || false,
        works_sunday: worker.works_sunday || false,
        new_password: '',
      });
      setShowPassword(false);
    }
  }, [worker, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        color: formData.color,
        cin_number: formData.cin_number,
        home_address: formData.home_address,
        payment_type: formData.payment_type,
        hourly_rate: formData.payment_type === 'hourly' && formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        percentage_rate: formData.payment_type === 'percentage' && formData.percentage_rate ? parseFloat(formData.percentage_rate) : null,
        works_saturday: formData.works_saturday,
        works_sunday: formData.works_sunday,
      };
      if (formData.new_password) {
        if (formData.new_password.length < 4) {
          toast.error(t('workers.passwordTooShort'));
          setLoading(false);
          return;
        }
        payload.new_password = formData.new_password;
      }
      
      await api.patch(`/workers/${worker.id}`, payload);
      toast.success(t('workers.updated'));
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to update worker:', err);
      toast.error(t('workers.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!worker) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('workers.editWorker')}</DialogTitle>
          <DialogDescription>{t('workers.editWorkerDesc')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('workers.firstName')}</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                required
                data-testid="edit-worker-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('workers.lastName')}</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                required
                data-testid="edit-worker-last-name"
              />
            </div>
          </div>
          
          {/* Login (Email) */}
          <div className="space-y-2">
            <Label>{t('workers.login')}</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
              data-testid="edit-worker-email"
            />
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label>{t('workers.newPassword')}</Label>
            <div className="flex gap-2">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.new_password}
                onChange={(e) => setFormData(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder={t('workers.leaveBlankToKeep')}
                className="flex-1 font-mono"
                data-testid="edit-worker-password"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{t('workers.leaveBlankToKeep')}</p>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label>{t('workers.phone')}</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              data-testid="edit-worker-phone"
            />
          </div>

          {/* Home Address */}
          <div className="space-y-2">
            <Label>{t('workers.homeAddress')}</Label>
            <Input
              value={formData.home_address}
              onChange={(e) => setFormData(prev => ({ ...prev, home_address: e.target.value }))}
              data-testid="edit-worker-address"
            />
          </div>

          {/* CIN Number */}
          <div className="space-y-2">
            <Label>{t('workers.cinNumber')}</Label>
            <Input
              value={formData.cin_number}
              onChange={(e) => setFormData(prev => ({ ...prev, cin_number: e.target.value }))}
              data-testid="edit-worker-cin"
            />
          </div>

          {/* Payment Type & Rate */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('workers.paymentType')}</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={formData.payment_type}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_type: e.target.value }))}
                data-testid="edit-worker-payment-type"
              >
                <option value="hourly">{t('workers.hourlyRate')}</option>
                <option value="percentage">{t('workers.percentageRate')}</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>
                {formData.payment_type === 'hourly' ? t('workers.hourlyRate') : t('workers.percentageRate')}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={formData.payment_type === 'hourly' ? formData.hourly_rate : formData.percentage_rate}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  [formData.payment_type === 'hourly' ? 'hourly_rate' : 'percentage_rate']: e.target.value 
                }))}
                placeholder={formData.payment_type === 'hourly' ? '350' : '15'}
                data-testid="edit-worker-rate"
              />
              {formData.payment_type === 'hourly' && formData.hourly_rate && parseFloat(formData.hourly_rate) < 17.40 && (
                <p className="text-xs text-amber-500 flex items-center gap-1">
                  ⚠️ Ниже минимальной ставки BC 2025 ($17.40/ч)
                </p>
              )}
            </div>
          </div>

          {/* Weekend Working Days */}
          <div className="space-y-2">
            <Label>{t('workers.workingDays') || 'Working Days'}</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-worker-saturday">
                <input
                  type="checkbox"
                  checked={formData.works_saturday}
                  onChange={(e) => setFormData(prev => ({ ...prev, works_saturday: e.target.checked }))}
                  className="h-4 w-4 rounded border-input accent-primary"
                />
                <span className="text-sm">{t('workers.saturday') || 'Saturday'}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-worker-sunday">
                <input
                  type="checkbox"
                  checked={formData.works_sunday}
                  onChange={(e) => setFormData(prev => ({ ...prev, works_sunday: e.target.checked }))}
                  className="h-4 w-4 rounded border-input accent-primary"
                />
                <span className="text-sm">{t('workers.sunday') || 'Sunday'}</span>
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t('workers.weekendHint') || 'Auto-scheduling will include weekends only for checked days'}
            </p>
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>{t('workers.color')}</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="w-12 h-10 p-1 cursor-pointer"
                data-testid="edit-worker-color"
              />
              <Input
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="flex-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading} data-testid="edit-worker-submit">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Pencil className="mr-2 h-4 w-4" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
